/**
 * Content-shaped loading placeholders.
 *
 * A centred spinner tells the reader "something is happening" and then shoves the page down
 * when the answer lands — the layout shift the CLS metric measures and the eye reads as a
 * glitch. A skeleton reserves the space the real content will occupy, so the swap is a
 * cross-fade rather than a jump.
 *
 * One `aria-busy` region per group carries the whole announcement; the bars themselves are
 * decorative and hidden, so a screen reader hears "Loading episodes" once instead of a dozen
 * empty boxes.
 */

interface GroupProps {
  /** How many placeholder rows/cards to draw. Match the typical result count. */
  count?: number;
  label?: string;
}

/** A single shimmering block. `w`/`h` accept any CSS length. */
export function SkeletonBlock({
  w = '100%',
  h = '1rem',
  radius,
}: {
  w?: string;
  h?: string;
  radius?: string;
}) {
  return (
    <span
      class="skeleton"
      aria-hidden="true"
      style={{ width: w, height: h, borderRadius: radius }}
    />
  );
}

export function SkeletonEpisodeList({ count = 4, label = 'Loading episodes' }: GroupProps) {
  return (
    <div class="skeleton-group episode-list" role="status" aria-busy="true" aria-label={label}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} class="episode-row skeleton-row" aria-hidden="true">
          <SkeletonBlock w="64px" h="64px" radius="var(--radius)" />
          <div class="skeleton-row__body">
            <SkeletonBlock w="min(78%, 22rem)" h="0.95rem" />
            <SkeletonBlock w="min(52%, 15rem)" h="0.8rem" />
            <SkeletonBlock w="min(34%, 9rem)" h="0.7rem" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonGrid({ count = 6, label = 'Loading podcasts' }: GroupProps) {
  return (
    <div class="skeleton-group podcast-grid" role="status" aria-busy="true" aria-label={label}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} class="skeleton-card" aria-hidden="true">
          <SkeletonBlock w="100%" h="auto" radius="var(--radius)" />
          <SkeletonBlock w="80%" h="0.85rem" />
          <SkeletonBlock w="55%" h="0.7rem" />
        </div>
      ))}
    </div>
  );
}

/** The artwork-plus-metadata block at the top of a show or episode page. */
export function SkeletonHeader({ label = 'Loading' }: { label?: string }) {
  return (
    <div class="skeleton-group show-header" role="status" aria-busy="true" aria-label={label}>
      <SkeletonBlock w="140px" h="140px" radius="var(--radius-lg)" />
      <div class="skeleton-row__body">
        <SkeletonBlock w="min(70%, 20rem)" h="1.6rem" />
        <SkeletonBlock w="min(40%, 12rem)" h="0.9rem" />
        <SkeletonBlock w="min(90%, 32rem)" h="0.8rem" />
        <SkeletonBlock w="min(80%, 28rem)" h="0.8rem" />
      </div>
    </div>
  );
}
